import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { PoolPlansTab, SharePlanUnionType } from '../../types/pool-plans.types';

export const useSharePlansByStakeholder = ({
  companyId,
  numberOfFetchedItems = 50,
  poolId,
  stakeholderId,
  selectedTab = PoolPlansTab.ACTIVE,
  enabled = true,
  vestByDate,
}: {
  companyId: string;
  enabled?: boolean;
  numberOfFetchedItems?: number;
  poolId: string;
  selectedTab?: PoolPlansTab;
  stakeholderId: string;
  vestByDate?: Date;
}) => {
  const {
    data: sharePlansData,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<{
    sharePlans: SharePlanUnionType[];
    totalPages: number;
  }>({
    queryKey: [
      QueryKey.GET_SHARE_PLANS_BY_STAKEHOLDER,
      companyId,
      poolId,
      stakeholderId,
      selectedTab,
      vestByDate,
    ],
    queryFn: ({ pageParam = 0 }) => {
      return ApiService.get(
        {
          endpoint: BackendRoute.COMPANIES,
          routePath: [companyId, 'pools', poolId, 'stakeholders', stakeholderId, 'share-plans'],
        },
        {
          queryParams: {
            vestByDate,
            take: numberOfFetchedItems,
            skip: pageParam,
          },
        },
      );
    },
    enabled: !!stakeholderId && !!poolId && !!companyId && enabled,
    initialPageParam: 0,
    refetchOnReconnect: true,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.totalPages > allPages.length ? allPages.length * numberOfFetchedItems : undefined,
  });
  const sharePlans = useMemo(
    () =>
      sharePlansData?.pages?.reduce<SharePlanUnionType[]>((acc, page) => {
        return [...acc, ...page.sharePlans];
      }, []),
    [sharePlansData?.pages],
  );

  return {
    sharePlans: sharePlans || [],
    hasNextPage,
    fetchNextPage,
    sharePlansData,
    isEmpty: sharePlans?.length === 0,
    numberOfFetchedItems,
  };
};
